/**
 * AURA Language Selector
 * Compact dropdown for switching the widget's display language
 */

import {
  SUPPORTED_LANGUAGES,
  getLanguage,
  setLanguage,
  onLanguageChange,
} from '../core/i18n';
import { getIcon } from './styles/icons';

export const LANGUAGE_SELECTOR_STYLES = `
  .aura-lang {
    position: relative;
  }

  .aura-lang__trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: var(--aura-radius-full);
    background: var(--aura-bg);
    color: var(--aura-text);
    border: 1px solid var(--aura-border);
    cursor: pointer;
    font-size: 11px;
    font-weight: 500;
    transition: background var(--aura-transition-fast);
    white-space: nowrap;
  }

  .aura-lang__trigger:hover {
    background: var(--aura-bg-hover);
  }

  .aura-lang__trigger svg {
    width: 10px;
    height: 10px;
  }

  .aura-lang__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: var(--aura-bg);
    border: 1px solid var(--aura-border);
    border-radius: var(--aura-radius-md);
    box-shadow: var(--aura-shadow);
    min-width: 160px;
    max-height: 220px;
    overflow-y: auto;
    z-index: 20;
    display: none;
  }

  .aura-lang__dropdown--open {
    display: block;
  }

  .aura-lang__option {
    display: flex;
    align-items: center;
    gap: var(--aura-spacing-sm);
    width: 100%;
    padding: 7px 12px;
    background: none;
    border: none;
    border-bottom: 1px solid var(--aura-border);
    color: var(--aura-text);
    font-size: var(--aura-font-size-sm);
    cursor: pointer;
    text-align: left;
    transition: background var(--aura-transition-fast);
  }

  .aura-lang__option:last-child {
    border-bottom: none;
  }

  .aura-lang__option:hover {
    background: var(--aura-bg-hover);
  }

  .aura-lang__option--active {
    background: var(--aura-primary-light);
    font-weight: 600;
  }

  .aura-lang__option-flag {
    font-size: 14px;
    line-height: 1;
  }
`;

export function createLanguageSelector(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'aura-lang';

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === getLanguage())
    ?? SUPPORTED_LANGUAGES[0]!;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'aura-lang__trigger';
  trigger.setAttribute('aria-label', 'Select language');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  const codeSpan = document.createElement('span');
  codeSpan.textContent = currentLang.code.toUpperCase();

  const chevron = document.createElement('span');
  chevron.innerHTML = getIcon('chevronDown');

  trigger.appendChild(codeSpan);
  trigger.appendChild(chevron);

  const dropdown = document.createElement('div');
  dropdown.className = 'aura-lang__dropdown';
  dropdown.setAttribute('role', 'listbox');
  dropdown.setAttribute('aria-label', 'Available languages');

  SUPPORTED_LANGUAGES.forEach((lang) => {
    const option = document.createElement('button');
    option.type = 'button';
    option.className = `aura-lang__option${lang.code === getLanguage() ? ' aura-lang__option--active' : ''}`;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(lang.code === getLanguage()));
    option.dataset.lang = lang.code;

    const optFlag = document.createElement('span');
    optFlag.className = 'aura-lang__option-flag';
    optFlag.textContent = lang.flag;

    const optLabel = document.createElement('span');
    optLabel.textContent = lang.label;

    option.appendChild(optFlag);
    option.appendChild(optLabel);

    option.addEventListener('click', () => {
      setLanguage(lang.code);
      dropdown.classList.remove('aura-lang__dropdown--open');
      trigger.setAttribute('aria-expanded', 'false');
    });

    dropdown.appendChild(option);
  });

  trigger.addEventListener('click', () => {
    const isOpen = dropdown.classList.contains('aura-lang__dropdown--open');
    dropdown.classList.toggle('aura-lang__dropdown--open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
  });

  onLanguageChange((lang) => {
    const newLang = SUPPORTED_LANGUAGES.find((l) => l.code === lang)
      ?? SUPPORTED_LANGUAGES[0]!;
    codeSpan.textContent = newLang.code.toUpperCase();

    dropdown.querySelectorAll('.aura-lang__option').forEach((opt) => {
      const el = opt as HTMLElement;
      const isActive = el.dataset.lang === lang;
      el.classList.toggle('aura-lang__option--active', isActive);
      el.setAttribute('aria-selected', String(isActive));
    });
  });

  container.appendChild(trigger);
  container.appendChild(dropdown);

  return container;
}
